import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface ChatRequest {
  message: string;
  session_id: string;
}

export interface ChatResponse {
  reply: string;
  reply_type?: string;
  terms_msg?: string;
  speak_text?: string;
  awaiting_confirmation?: boolean;
  awaiting_mobile?: boolean;
  quote_summary?: any;
  quote_id?: string;
  rating?: any;
  init_status?: string;
}

export interface SttResponse {
  transcript: string;
  confidence?: number;
  status?: string;
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class ChatService {
  private readonly BASE_URL = environment.apiBaseUrl;

  constructor(private http: HttpClient) {}

  // ── Text chat ────────────────────────────────────────────────────────────
  sendMessage(message: string, sessionId: string): Observable<ChatResponse> {
    return this.http.post<ChatResponse>(`${this.BASE_URL}/chat`, {
      message,
      session_id: sessionId
    });
  }

  // ── Azure STT: send raw WAV blob → get transcript ────────────────────────
  speechToText(wavBlob: Blob): Observable<SttResponse> {
    // Browser converts to WAV before sending — send as multipart field="audio"
    const formData = new FormData();
    formData.append('audio', wavBlob, 'recording.wav');
    return this.http.post<SttResponse>(`${this.BASE_URL}/stt`, formData);
  }

  // ── Azure TTS: send plain text → get MP3 blob ────────────────────────────
  textToSpeech(text: string): Observable<Blob> {
    return new Observable(observer => {
      this.http.post(
        `${this.BASE_URL}/tts`,
        { text },
        { responseType: 'arraybuffer' }   // get raw bytes, not blob — avoids MIME issues
      ).subscribe({
        next: (buffer: ArrayBuffer) => {
          // Explicitly type as audio/mpeg so Chrome can play it
          const blob = new Blob([buffer], { type: 'audio/mpeg' });
          observer.next(blob);
          observer.complete();
        },
        error: (err) => observer.error(err)
      });
    });
  }

  // ── Session management ───────────────────────────────────────────────────
  clearSession(sessionId: string): Observable<any> {
    return this.http.delete(`${this.BASE_URL}/session/${sessionId}`);
  }

  healthCheck(): Observable<any> {
    return this.http.get(`${this.BASE_URL}/health`);
  }
}